import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { FormikProps, useFormik } from 'formik';

import { TFetchInspectionProps } from '../services/Inspection/types';
import {
  useFetchInspection,
  useFetchInspectionQuantity,
} from '../services/Inspection/useInspectionService';

type Quantity = ReturnType<typeof useFetchInspectionQuantity>;
type Inspections = ReturnType<typeof useFetchInspection>;

export interface UseInspectionsHook {
  formik: FormikProps<{ searchTerm: string; searchField: string }>;
  searchTerm: string;
  searchField: string;
  quantityData: Quantity['data'];
  quantityLoading: Quantity['loading'];
  quantityError: Quantity['error'];
  fetchInspectionQuantity: Quantity['fetchInspectionQuantity'];

  inspectionsData: Inspections['data'];
  inspectionsLoading: Inspections['loading'];
  inspectionsError: Inspections['error'];

  currentPage: number;
  pageSize: number;
  setPageSize: (n: number) => void;
  setCurrentPage: (n: number) => void;
  handleGetInspections: (today?: boolean) => void;
}

const useInspections = (): UseInspectionsHook => {
  const {
    data: quantityData,
    loading: quantityLoading,
    error: quantityError,
    fetchInspectionQuantity,
  } = useFetchInspectionQuantity();

  const {
    data: inspectionsData,
    loading: inspectionsLoading,
    error: inspectionsError,
    fetchInspection,
  } = useFetchInspection();

  const [params, setParams] = useState<TFetchInspectionProps>();

  const formik = useFormik({
    initialValues: { searchTerm: '', searchField: 'property_code' },
    onSubmit: (values) => {
      setParams({
        page: 1,
        page_size: params?.page_size,
        ...(values.searchField === 'property_code'
          ? { property_code: values.searchTerm }
          : { owner_name: values.searchTerm }),
      });
    },
  });

  useEffect(() => {
    if (params) fetchInspection(params);
  }, [params]);

  useEffect(() => {
    fetchInspectionQuantity();
  }, [fetchInspectionQuantity]);

  const handleGetInspections = (today = false) => {
    setParams({
      page: 1,
      page_size: params?.page_size || '10',
      ...(today ? { start_date: format(new Date(), 'yyyy-MM-dd') } : {}),
    });
    formik.setFieldValue('searchTerm', '');
  };

  const setPageSize = (n: number) => setParams((p) => ({ ...p, page: 1, page_size: String(n) }));
  const setCurrentPage = (n: number) => setParams((p) => ({ ...p, page: n }));

  const currentPage = Number(params?.page);
  const pageSize = Number(params?.page_size);

  return {
    formik,
    searchTerm: formik.values.searchTerm,
    searchField: formik.values.searchField,
    quantityData,
    quantityLoading,
    quantityError,
    fetchInspectionQuantity,
    inspectionsData,
    inspectionsLoading,
    inspectionsError,
    currentPage,
    pageSize,
    setPageSize,
    setCurrentPage,
    handleGetInspections,
  };
};

export default useInspections;
